import type { Message} from '../Communication';

import { sendMessage } from '../Communication';

/** @internal */
export interface DescriptorAction {
    descriptor: string;
}

/** @internal */
export interface PathActions {
    path: string;
}

/** @internal */
export interface CopyAction {
    source: string;
    destination: string;
    override: boolean;
}

export type OpenMode = 'r' | 'w';
export type Converter = '' | 'base64';

/** @internal */
export interface OpenAction extends PathActions {
    mode: OpenMode;
    converter: Converter;
}

/** @internal */
export interface ReadAction extends DescriptorAction {
    size: number;
}

/** @internal */
export interface WriteAction extends DescriptorAction {
    data: string;
}

export function close(descriptor: string): Promise<void> {
    return sendMessage<Message<DescriptorAction>>({
        type: 'fs',
        action: 'close',
        arguments: {
            descriptor: descriptor
        }
    });
}

export function copy(src: string, dst: string, override: boolean): Promise<void> {
    return sendMessage<Message<CopyAction>>({
        type: 'fs',
        action: 'copy',
        arguments: {
            source: src,
            destination: dst,
            override: override
        }
    });
}

export async function move(src: string, dst: string): Promise<void> {
    // FixMe: define app api!
    await copy(src, dst, true);
    await remove(src);
}

export function flush(descriptor: string): Promise<void> {
    return sendMessage<Message<DescriptorAction>>({
        type: 'fs',
        action: 'flush',
        arguments: {
            descriptor: descriptor
        }
    });
}

export function list(path: string): Promise<Array<string>> {
    return sendMessage<Message<PathActions>, Array<string>>({
        type: 'fs',
        action: 'list',
        arguments: {
            path: path
        }
    });
}

export function open(path: string, mode: OpenMode, converter: Converter): Promise<string> {
    return sendMessage<Message<OpenAction>, string>({
        type: 'fs',
        action: 'open',
        arguments: {
            path: path,
            mode: mode,
            converter: converter
        }
    });
}

export function read(descriptor: string, length: number): Promise<string> {
    return sendMessage<Message<ReadAction>, string>({
        type: 'fs',
        action: 'read',
        arguments: {
            descriptor: descriptor,
            size: length
        }
    });
}

export function remove(path: string): Promise<void> {
    return sendMessage<Message<PathActions>>({
        type: 'fs',
        action: 'remove',
        arguments: {
            path: path
        }
    });
}

export function size(path: string): Promise<number> {
    return sendMessage<Message<PathActions>, number>({
        type: 'fs',
        action: 'size',
        arguments: {
            path: path
        }
    });
}

export function write(descriptor: string, data: string): Promise<void> {
    return sendMessage<Message<WriteAction>>({
        type: 'fs',
        action: 'write',
        arguments: {
            descriptor: descriptor,
            data: data
        }
    });
}

// opens specified Folder (UWP only)
export function showFolder(path: string): Promise<void> {
    return sendMessage<Message<PathActions>>({
        type: 'fs',
        action: 'show_fs_root',
        arguments: {
            path: path
        }
    });
}

/** @internal */
export interface ZipAction {
    src: string;
    target: string;
    // not a valid property for the native app
    _debugFsOnly_ignoreAndLogErrors?: boolean;
}

/** @internal */
export type UnzipAction = ZipAction;

export function zip(target: string, src: string, _debugFsOnly_ignoreAndLogErrors?: boolean): Promise<void> {
    return sendMessage<Message<ZipAction>>({
        type: 'fs',
        action: 'zip',
        arguments: {
            src,
            target,
            _debugFsOnly_ignoreAndLogErrors
        }
    });
}

export function unzip(src: string, target: string): Promise<void> {
    return sendMessage<Message<UnzipAction>>({
        type: 'fs',
        action: 'zip',
        arguments: {
            src,
            target
        }
    });
}

export interface HashFileInfo {
    size: number;
    hash: string;
}
export function hash(paths: Array<string>, hashType: 'md5' = 'md5'): Promise<Array<HashFileInfo>> {
    return sendMessage<Message<{type: string, paths: Array<string>}>, Array<HashFileInfo>>({
        type: 'fs',
        action: 'hash',
        arguments: {
            type: hashType,
            paths
        }
    });
}

export function syncDirectories(source: string, destination: string): Promise<void> {
    return sendMessage<Message<{source: string; destination: string}>>({
        type: 'fs',
        action: 'syncDirectories',
        arguments: {
            source,
            destination
        }
    });
}

export function hasSyncLock(directory: string): Promise<boolean> {
    return sendMessage<Message<{directory: string}>, boolean>({
        type: 'fs',
        action: 'hasSyncLock',
        arguments: {
            directory
        }
    });
}

export function createSyncLock(directory: string): Promise<void> {
    return sendMessage<Message<{directory: string}>, void>({
        type: 'fs',
        action: 'createSyncLock',
        arguments: {
            directory
        }
    });
}

export function removeSyncLock(directory: string): Promise<void> {
    return sendMessage<Message<{directory: string}>, void>({
        type: 'fs',
        action: 'removeSyncLock',
        arguments: {
            directory
        }
    });
}